// --------------------------------------------------------
// USER ROLE - UserRole
// --------------------------------------------------------

// --------------------------------------------------------
// IMPORTS
// --------------------------------------------------------
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import LabelInput from "../../components/labelInput/LabelInput";
import ContainerWithSidebar from "../../components/UI/containerWithSidebar/ContainerWithSidebar";
import {
  GeneralContainer,
  H1,
  Text,
} from "../../components/UI/generalLayout/Layout.styled";
import {
  Container,
  RowContainer,
} from "../company/companyRegister/CompanyRegister.styled";
import SearchIcon from "@mui/icons-material/Search";
import {
  CompanyListContainer,
  CompanyListItemContainer,
} from "../company/CompanyList.styled";
import UserListItem from "../../components/userListItem/UserListItem";
import CustomDropdown from "../../components/dropdown/CustomDropdown";
import Colors from "../../constants/Colors";
import Company from "../../models/core/company/Company";
import { useSelector } from "react-redux";
import * as userRoleActions from "../../store/actions/userRole";
import * as companyActions from "../../store/actions/company";
import { useAppDispatch } from "../..";
import PersonCompany from "../../models/core/userRole/PersonCompany";
import useResponsiveInfo from "../../hooks/useResponsiveInfo";
import ListHeader from "../../components/itemList/ListHeaders";
import ScreenHeader from "../../components/UI/screenHeader/ScreenHeader";
import { useNavigate } from "react-router-dom";
import { Paths } from '../../enums/Paths';
import { GenericSolidButton } from "../../components/UI/buttons/Button.styled";

// --------------------------------------------------------
// USER ROLE INTERFACE
// --------------------------------------------------------

/**
 * A component that renders a Screen.
 *
 * @param props Properties
 * @param props.route The route
 * @param props.navigation The navigator
 * @returns The component JSX.
 */
function UserRole(): JSX.Element {
  // --------------------------------------------------------
  // USER ROLE PRIVATE DECLARATIONS
  // --------------------------------------------------------
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userCompanies }: any = useSelector((state: any) => state.login);

  const [searchUserQuery, setSearchUserQuery] = useState("");
  const [companiesToFilterBy, setCompaniesFilter] = useState<any[]>([]);
  const [rolesToFilterBy, setRolesFilter] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastSearchedQuery = useRef<string | null>(null);
  const { isMobile } = useResponsiveInfo();
  const [userRoleHeaderFields] = useState([
    { name: "", width: "1%", align: "left" },
    { name: "Nome", width: "20%", align: "left" },
    { name: "CPF", width: "15%", align: "left" },
    { name: "Empresa", width: "23%", align: "left" },
    { name: "", width: "5%", align: "left" },
    { name: "", width: "5%", align: "left" },
    { name: "Perfil", width: "17%", align: "left" },
  ]);

  const companyListCombo: Company[] = useSelector(
    (state: any) => state.company.companies
  );
  const roles: string[] = useSelector(
    (state: any) => state.userRole.rolesCombo
  );
  const usersWithRoles: PersonCompany[] = useSelector(
    (state: any) => state.userRole.usersWithRoles
  );

  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      const listaNomes: string[] = userCompanies.map(
        (company: any) => company.nome
      );
      await dispatch(userRoleActions.fetchAllRoles());
      await dispatch(userRoleActions.filterUsersByCompanyOrRole(listaNomes, []));
      await dispatch(companyActions.getCompaniesToUserRoleCombo());
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, userCompanies]);

  const filterDataByCompanyOrRole = useCallback(async () => {
    const listaNomes: string[] = userCompanies.map(
      (company: any) => company.nome
    );

    setIsLoading(true);
    try {
      if (companiesToFilterBy.length <= 0) {
        await dispatch(
          userRoleActions.filterUsersByCompanyOrRole(listaNomes, rolesToFilterBy)
        );
      } else {
        await dispatch(
          userRoleActions.filterUsersByCompanyOrRole(
            companiesToFilterBy,
            rolesToFilterBy
          )
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [companiesToFilterBy, rolesToFilterBy, dispatch, userCompanies]);

  useEffect(() => {
    getData();
  }, []);

  const filterDataByNameOrCpf = useCallback(async (query: string) => {
    if (query.length < 3 && query !== "") {
      return;
    }
    if (lastSearchedQuery.current === query) {
      return;
    }

    lastSearchedQuery.current = query;
    setIsLoading(true);
    try {
      await dispatch(userRoleActions.filterUserByNameOrCPF(query));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const filterCompanies = () => {
    const companies: any[] = [];

    companyListCombo.forEach(company => {
      if (userCompanies.find((c: any) => c.nome === company.nomeFantasia)) {
        companies.push({
          label: company.nomeFantasia,
          value: company.nomeFantasia,
        });
      }
    });

    return companies;
  };

  const navigateToRegisterCompany = () => {
    return navigate(Paths.RegisterCompany)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      filterDataByNameOrCpf(searchUserQuery);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchUserQuery, filterDataByNameOrCpf]);

  return (
    <ContainerWithSidebar>
      <GeneralContainer style={{ marginBottom: "2%" }}>
        <ScreenHeader
          screenTitle={"Gerenciar Usuários"}
          hasRightButton={false}
          rightButtonText=""
          onRightButtonClick={() => {}}
        />
        <RowContainer style={{ marginBottom: "1%" }}>
          <LabelInput
            value={searchUserQuery}
            style={{ marginRight: 2 }}
            placeholder="Busca de nome"
            onChangeText={(event: any) => {
              setSearchUserQuery(event.target.value);
            }}
            onKeyDown={(event: any) => {
              if (event.key === "Enter") {
                filterDataByNameOrCpf(searchUserQuery);
              }
            }}
            rightIcon={
              <div 
                style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                onClick={() => filterDataByNameOrCpf(searchUserQuery)}
              >
                <SearchIcon />
              </div>
            }
          />
          {/* <CustomDropdown
            value={companiesToFilterBy}
            onChange={(value: any) => {
              setCompaniesFilter(value);
            }}
            bottomButtonAction={filterDataByCompanyOrRole}
            items={filterCompanies()}
            disabled={false}
            multiple={true}
            consideraAtribuicao={false}
            backgroundColor={Colors.SPM50}
            textColor={Colors.SPM600}
            placeholder="Filtrar empresas"
            containerStyle={{
              width: isMobile ? "100%" : 215,
              marginRight: !isMobile ? 8 : 0,
              marginTop: isMobile ? 12 : 0,
            }}
            bottomButtonMsg={"Filtrar empresas"}
          /> */}
          <CustomDropdown
            value={rolesToFilterBy}
            onChange={(value: any) => {
              setRolesFilter(value);
            }}
            bottomButtonAction={filterDataByCompanyOrRole}
            items={roles}
            consideraAtribuicao={false}
            placeholder="Filtrar perfis"
            containerStyle={{
              width: isMobile ? "100%" : 215,
              marginTop: isMobile ? 12 : 0,
            }}
            backgroundColor={Colors.SPM50}
            textColor={Colors.SPM600}
            disabled={false}
            multiple={true}
            bottomButtonMsg={"Filtrar perfis"}
          />
        </RowContainer>
        {searchUserQuery.length > 0 && (
          <Text color={Colors.lightBlue}>
            Resultados para: {searchUserQuery}{" "}
          </Text>
        )}

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px" }}>
            <CircularProgress color="primary" />
          </div>
        ) : usersWithRoles !== null && usersWithRoles.length > 0 ? (
          <>
            {!isMobile && <ListHeader fields={userRoleHeaderFields} />}
            <CompanyListContainer>
              <CompanyListItemContainer>
                {usersWithRoles
                  .filter(emp => emp.empresa?.id === userCompanies[0].id)
                  .map(user => {
                    return (
                      <React.Fragment key={user.id}>
                        <UserListItem
                          item={new PersonCompany().fromJSON({ ...user })}
                          roles={roles}
                        />
                      </React.Fragment>
                    );
                  })}
              </CompanyListItemContainer>
            </CompanyListContainer>
          </>
        ) : (
          <Text>Nenhum resultado encontrado</Text>
        )}
      </GeneralContainer>
    </ContainerWithSidebar>
  );
}

export default UserRole;
